import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { RefreshCw, Package, Flame } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function sizeKg(sizeStr) {
  return parseFloat(sizeStr) || 0;
}

function GodownTable({ name, rows }) {
  const totalFilled = rows.reduce((s, r) => s + (r.filled  || 0), 0);
  const totalMT     = rows.reduce((s, r) => s + (r.mt       || 0), 0);
  const totalDef    = rows.reduce((s, r) => s + (r.defective || 0), 0);
  const totalKg     = rows.reduce((s, r) => s + (r.filled  || 0) * sizeKg(r.size), 0);

  return (
    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden mb-5">
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 bg-gray-50">
        <div className="flex items-center gap-2">
          <span className="small-label text-gray-400">Godown</span>
          <span className="font-black text-[15px] tracking-tight">{name}</span>
        </div>
        <RefreshCw size={13} className="text-gray-300 cursor-pointer" />
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-100">
              {['Brand', 'Size', 'Filled', 'MT', 'Defective', 'KG'].map(h => (
                <th key={h} className="px-4 py-2.5 text-left small-label text-gray-400 whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} className="border-b border-gray-50 hover:bg-gray-50 transition-colors">
                <td className="px-4 py-2.5 text-[13px] font-medium text-gray-700">{r.brand}</td>
                <td className="px-4 py-2.5 text-[13px] text-gray-600">{r.size}</td>
                <td className="px-4 py-2.5 text-[13px] font-semibold text-green-700">
                  {r.filled > 0 ? r.filled : <span className="text-gray-400">0</span>}
                </td>
                <td className="px-4 py-2.5 text-[13px] font-semibold text-amber-700">
                  {r.mt > 0 ? r.mt : <span className="text-gray-400">0</span>}
                </td>
                <td className="px-4 py-2.5 text-[13px]">
                  {(r.defective || 0) > 0
                    ? <span className="text-red-600 font-semibold">{r.defective}</span>
                    : <span className="text-gray-400">0</span>}
                </td>
                <td className="px-4 py-2.5 text-[13px] text-gray-700">
                  {((r.filled || 0) * sizeKg(r.size)).toFixed(2)}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="bg-gray-50 border-t border-gray-200">
              <td className="px-4 py-2.5 text-[12px] font-bold text-gray-700 uppercase tracking-wide" colSpan={2}>Total</td>
              <td className="px-4 py-2.5 text-[13px] font-bold text-green-700">{totalFilled}</td>
              <td className="px-4 py-2.5 text-[13px] font-bold text-amber-700">{totalMT}</td>
              <td className="px-4 py-2.5 text-[13px] font-bold text-red-700">{totalDef || '—'}</td>
              <td className="px-4 py-2.5 text-[13px] font-bold text-gray-700">{totalKg.toFixed(2)}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}

export default function LiveStock() {
  const { authHeader } = useAuth();
  const [stockByGodown, setStockByGodown] = useState({});
  const [loading, setLoading]             = useState(true);
  const [lastRefresh, setLastRefresh]     = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/stock`, { headers: authHeader() });
      setStockByGodown(res.data);
      setLastRefresh(new Date().toLocaleTimeString('en-IN'));
    } catch (err) {
      console.error('Stock load error', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const allRows     = Object.values(stockByGodown).flat();
  const totalFilled = allRows.reduce((s, r) => s + (r.filled   || 0), 0);
  const totalMT     = allRows.reduce((s, r) => s + (r.mt        || 0), 0);
  const totalDef    = allRows.reduce((s, r) => s + (r.defective || 0), 0);
  const totalKg     = allRows.reduce((s, r) => s + (r.filled    || 0) * sizeKg(r.size), 0);

  const godownNames = Object.keys(stockByGodown);

  return (
    <div className="px-6 py-6">
      <div className="flex items-start justify-between mb-5 gap-4 flex-wrap">
        <div>
          <div className="small-label text-gray-400 mb-1 flex items-center gap-1.5">
            <Package size={11} /> Live Stock
          </div>
          <h1 className="font-black text-3xl tracking-tight">Live Stock</h1>
          <p className="text-xs text-gray-400 mt-1">
            {loading ? 'Loading…' : `Updated ${lastRefresh}`}
          </p>
        </div>
        <button onClick={load} className="btn-secondary flex items-center gap-1.5 text-sm" disabled={loading}>
          <RefreshCw size={13} className={loading ? 'animate-spin' : ''} /> Refresh
        </button>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
        {[
          { label: 'Total Filled',    value: totalFilled,          color: 'text-orange-600' },
          { label: 'Total MT',        value: totalMT,              color: 'text-gray-700' },
          { label: 'Defective',       value: totalDef,             color: 'text-red-600' },
          { label: 'Total KG (LPG)', value: totalKg.toFixed(2),   color: 'text-gray-700' },
        ].map(s => (
          <div key={s.label} className="border border-gray-200 rounded-lg p-4 bg-white">
            <div className="small-label text-gray-400 mb-2">{s.label}</div>
            <div className={`font-black text-2xl ${s.color}`}>{s.value}</div>
          </div>
        ))}
      </div>

      {loading && (
        <div className="flex items-center justify-center py-16">
          <RefreshCw size={20} className="animate-spin text-gray-400" />
        </div>
      )}

      {!loading && godownNames.length === 0 && (
        <div className="text-center py-16 text-gray-400 border border-dashed border-gray-200 rounded-lg">
          No stock data yet. Create a Gas Purchase entry to add stock.
        </div>
      )}

      {godownNames.map(name => (
        <GodownTable key={name} name={name} rows={stockByGodown[name] || []} />
      ))}
    </div>
  );
}
